import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { BusinessUnitToggle } from "@/shared/types";
import { axiosApi } from "@/Utils";
import { Layer } from "@/Utils/hierarchy";

import { SharedRevenueChannelTotalROIMap } from "../types";
import { selectSharedRevenue } from "./helpers/selectRois";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";

export interface PaidMediaNode {
  customer: string;
  channel: string;
  geography: string;
  division: string;
  brand: string;
  category: string;
  product: string;
  node_uuid: string;
  data: {
    revenue: number;
    spend: number;
    unit: number;
    sampleSize: number;
    adstockPeak: number;
    adstockDuration: number;
  };
}

export interface SharedRevenueData {
  spend: number;
  revenue: number;
  adstockPeak: number;
  adstockDuration: number;
}

export interface SharedRevenueDto {
  channel: string;
  geography: string;
  data: SharedRevenueData;
  layer?: Layer;
}

const getSharedRevenue = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  channels,
  funnelStage,
  geographies,
  businessUnitToggle,
}: {
  brand: string;
  category: string;
  product: string;
  startDate?: string | null;
  endDate?: string | null;
  channels?: string[];
  funnelStage?: string;
  geographies?: string[];
  businessUnitToggle?: BusinessUnitToggle;
}): Promise<{
  aggregated: SharedRevenueChannelTotalROIMap;
  split: SharedRevenueChannelTotalROIMap;
  totals: {
    totalRevenue: number;
    totalBrandRevenue: number;
    totalCategoryRevenue: number;
    totalProductRevenue: number;
  };
}> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);

  // If funnelStage is 'all', append brand, category, and product
  if (funnelStage === "all") {
    if (brand) params.append("funnelStages", "brand");
    if (category) params.append("funnelStages", "category");
    if (product) params.append("funnelStages", "product");
  } else if (funnelStage) {
    params.append("funnelStages", funnelStage);
  }

  if (channels && channels.length > 0) {
    channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  if (geographies && geographies.length > 0) {
    geographies.forEach((geography) => {
      params.append("geographies", geography);
    });
  }

  return axiosApi.get(`/api/roi/shared?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

const getSharedRevenueOld = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  shouldShiftSpend,
  channels,
  funnelStage,
  geographies,
  businessUnitToggle,
}: {
  brand: string;
  category: string;
  product: string;
  startDate?: string | null;
  endDate?: string | null;
  shouldShiftSpend: boolean;
  channels?: string[];
  funnelStage?: string;
  geographies?: string[];
  businessUnitToggle?: BusinessUnitToggle;
}): Promise<{
  aggregated: SharedRevenueChannelTotalROIMap;
  split: SharedRevenueChannelTotalROIMap;
  totals: {
    totalRevenue: number;
    totalBrandRevenue: number;
    totalCategoryRevenue: number;
    totalProductRevenue: number;
  };
}> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    startDate,
    endDate,
    shouldShiftSpend,
    channels,
    funnelStage,
    geographies,
    businessUnitToggle,
  };

  return axiosApi.get("/api/v2/roi/shared/v2", {
    params: payload,
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

export const useSharedRevenue = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  shouldShiftSpend,
  channels,
  funnelStage,
  geographies,
  businessUnitToggle,
  config,
}: {
  brand: string;
  category: string;
  product: string;
  startDate?: string | null;
  endDate?: string | null;
  shouldShiftSpend: boolean;
  channels?: string[];
  funnelStage?: string;
  geographies?: string[];
  businessUnitToggle?: BusinessUnitToggle;
  config?: QueryConfig<typeof getSharedRevenue>;
}) => {
  const endpointType = useModuleEndpoint("channel-roi");

  const queryKey = useMemo(
    () => [
      "shared-revenue",
      brand,
      category,
      product,
      startDate,
      endDate,
      shouldShiftSpend,
      channels,
      funnelStage,
      geographies,
      businessUnitToggle,
    ],
    [
      brand,
      category,
      product,
      startDate,
      endDate,
      shouldShiftSpend,
      channels,
      funnelStage,
      geographies,
      businessUnitToggle,
    ]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return getSharedRevenue({
        brand,
        category,
        product,
        startDate,
        endDate,
        channels,
        funnelStage,
        geographies,
        businessUnitToggle,
      });
    } else {
      return getSharedRevenueOld({
        brand,
        category,
        product,
        startDate,
        endDate,
        shouldShiftSpend,
        channels,
        funnelStage,
        geographies,
        businessUnitToggle,
      });
    }
  }, [
    brand,
    category,
    product,
    startDate,
    endDate,
    channels,
    funnelStage,
    geographies,
    businessUnitToggle,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
    select: selectSharedRevenue,
  });
};
